import {
    AfterViewInit,
    Directive,
    ElementRef,
    Inject,
    Input,
    Renderer2,
} from "@angular/core";
import "@angular/localize/init";

import { INFRA_SETTINGS } from "@hermes/app-core";
import { InfraSettings } from "@hermes/env-infra";
import { MarkingPosition } from "@hermes/states/personalization-page";

/**
 * The objective of this directive is to be able to concatenate several translation keys and set their value in an html attribute
 */
@Directive({
    selector: "[hI18nAsset]",
})
export class I18nAssetAttributeDirective implements AfterViewInit {
    /** The html attribute that must be updated with concatenated translation keys */
    @Input()
    public hI18nAttr: string = "alt";

    /** Image tag: front, back ... */
    @Input()
    public tag?: string;

    @Input()
    public imageTitle?: string;

    @Input()
    public index: number = 0;

    @Input()
    public imageListSize?: number;

    @Input()
    public url!: string;

    constructor(
        private elementReference: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        @Inject(INFRA_SETTINGS) public settings: InfraSettings,
    ) {}

    public ngAfterViewInit(): void {
        if (!this.tag) {
            return;
        }

        // Set Dom property for given element
        this.renderer.setProperty(
            this.elementReference.nativeElement,
            this.hI18nAttr,
            this.concatTradKeys(this.tag),
        );
    }

    public concatTradKeys(tag: string): string {
        const imageView = $localize`:@@hermes.accessibility.product-image-view:View`;
        const imageType = $localize`${this.getTradKey(this.getImageType(tag))}`;
        return `${imageView}: ${imageType}, ${this.imageTitle}`;
    }

    public getTradKey(tag: string): string {
        switch (tag) {
            case "front": {
                return $localize`:@@hermes.accessibility.product-image-front:front`;
            }
            case "side": {
                return $localize`:@@hermes.accessibility.product-image-side:side`;
            }
            case "above": {
                return $localize`:@@hermes.accessibility.product-image-above:above`;
            }
            case "below": {
                return $localize`:@@hermes.accessibility.product-image-below:below`;
            }
            case "back": {
                return $localize`:@@hermes.accessibility.product-image-back:back`;
            }
            case "worn":
            case "wornsquare": {
                return $localize`:@@hermes.accessibility.product-image-worn:worn`;
            }
            case "folded": {
                return $localize`:@@hermes.accessibility.product-image-folded:folded`;
            }
            case "sport": {
                return $localize`:@@hermes.accessibility.product-image-sport:sport`;
            }
            case "zoom": {
                return $localize`:@@hermes.accessibility.product-image-zoom:zoom`;
            }
            case "frontopen": {
                return $localize`:@@hermes.accessibility.product-image-front-open:frontopen`;
            }
            case "frontclose": {
                return $localize`:@@hermes.accessibility.product-image-front-close:frontclose`;
            }
            case "backopen": {
                return $localize`:@@hermes.accessibility.product-image-back-open:backopen`;
            }
            case "backclose": {
                return $localize`:@@hermes.accessibility.product-image-back-close:backclose`;
            }
            case "info": {
                return $localize`:@@hermes.accessibility.product-image-info:info`;
            }
            case "detail": {
                return $localize`:@@hermes.accessibility.product-image-detail:detail`;
            }
            case "crop": {
                return $localize`:@@hermes.accessibility.product-image-crop:crop`;
            }
            case "context": {
                return $localize`:@@hermes.accessibility.product-image-context:context`;
            }
            case "flat": {
                return $localize`:@@hermes.accessibility.product-image-flat:flat`;
            }
            case "video": {
                return $localize`:@@hermes.accessibility.product-image-video:video`;
            }
            case "store": {
                return $localize`:@@hermes.accessibility.store-display:Store image ${this.index}:INTERPOLATION: of ${this.imageListSize}:INTERPOLATION_1:`;
            }
            case MarkingPosition.ZOOM_BACK: {
                return $localize`:@@hermes.accessibility.product-image-zoom:Zoom view`;
            }
            default: {
                return "";
            }
        }
    }

    /**
     * Get type of current image based on current url OR image tag
     */
    public getImageType(tag: string): string {
        if (tag) {
            return tag;
        }
        if (this.url.match("store-")) {
            return "store";
        }

        if (this.url.match("-AVS")) {
            return "video";
        }
        if (
            this.url.startsWith(
                this.settings.personalizationPlatformeUrl.split("/api")[0],
            )
        ) {
            const urlParameters = new URLSearchParams(this.url);
            return urlParameters.get("frame") ?? "";
        }
        const splittedUrl = this.url.split("_");

        if (!splittedUrl[1]) {
            return "";
        }

        return splittedUrl[1].includes("composite")
            ? this.getViewFromComposite(
                  `${splittedUrl[1]}_${splittedUrl[2].slice(0, 1)}`,
              )
            : splittedUrl[1];
    }

    /**
     * get view from composite
     */
    public getViewFromComposite(view: string): string {
        switch (view) {
            case "composite_5": {
                return "front";
            }
            case "composite_6": {
                return "side";
            }
            case "composite_7": {
                return "back";
            }
            case "composite_8": {
                return "sport";
            }
            default: {
                return view;
            }
        }
    }
}
